import * as React from "react";
import styled from "@emotion/styled";
import {
  Collapsible as RadixCollapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible";

import UIcon, { IconProps } from "../../../lib/icons/untitled_ui/UIcon";
import { smoothTransition } from "../../../styles/animations";
import { Text } from "../../../styles/utility-components";
import Divider from "../divider/Divider";

/**
 * Props for the Collapsible component
 * @property title - Main text to display in the header
 * @property subtitle - Optional subtitle text to display below the title
 * @property chevronText - Text to display before the chevron icon
 * @property chevronPosition - Vertical alignment of the chevron inside the header
 * @property children - Content to render inside the collapsible
 * @property defaultOpen - Whether the collapsible is open by default (uncontrolled mode)
 * @property open - Whether the collapsible is open (controlled mode)
 * @property onOpenChange - Callback when open state changes
 * @property disabled - Whether the collapsible is disabled
 * @property icon - Optional icon to display before the title
 * @property iconProps - Additional props for the icon
 * @property isBordered - Whether to show a border around the collapsible
 */

type ChevronPosition = "top" | "center";

type CollapsibleProps = {
  title: React.ReactNode;
  subtitle?: string;
  chevronText?: string;
  chevronPosition?: ChevronPosition;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  icon?: IconProps["icon"];
  iconProps?: Omit<IconProps, "icon">;
  isBordered?: boolean;
  dividerOnOpen?: boolean;
  headerContentProps?: { padding?: string };
};

/**
 * Collapsible component for showing/hiding content with a smooth animation.
 * Built on top of Radix UI's Collapsible primitive for robust accessibility
 * and keyboard navigation.
 *
 * Features:
 * - Controlled and uncontrolled modes
 * - Support for title
 * - Optional icon support
 * - Disabled state
 * - Full keyboard navigation
 * - ARIA compliant
 *
 * @example
 * ```tsx
 * <Collapsible
 *   title="Account Settings"
 *   defaultOpen={false}
 *   icon="user"
 * >
 *   <AccountSettingsForm />
 * </Collapsible>
 * ```
 *
 * Keyboard Interactions:
 * - Enter/Space: Toggles the collapsible
 * - Tab: Moves focus to the next focusable element
 *
 * @param props - The collapsible component props
 */
export default function Collapsible({
  title,
  subtitle,
  chevronText,
  chevronPosition = "center",
  children,
  defaultOpen,
  open,
  onOpenChange,
  disabled = false,
  icon,
  iconProps,
  isBordered = false,
  dividerOnOpen = true,
  headerContentProps,
}: CollapsibleProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

  const handleOpenChange = (newOpen: boolean) => {
    if (disabled) return;

    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const isOpen = open ?? internalOpen;

  return (
    <Root open={isOpen} onOpenChange={handleOpenChange} isBordered={isBordered}>
      <Trigger disabled={disabled}>
        <HeaderContainer chevronPosition={chevronPosition}>
          <HeaderContent {...headerContentProps}>
            {icon && <UIcon icon={icon} size={16} {...iconProps} />}
            <TitleContainer>
              {typeof title === "string" ? <Title disabled={disabled}>{title}</Title> : title}
              {subtitle && (
                <Subtitle disabled={disabled} description>
                  {subtitle}
                </Subtitle>
              )}
            </TitleContainer>
          </HeaderContent>
          <RightContent>
            {chevronText && (
              <Subtitle disabled={disabled} description>
                {chevronText}
              </Subtitle>
            )}
            <ChevronContainer disabled={disabled} open={isOpen}>
              <UIcon icon="chevronDown" size={16} />
            </ChevronContainer>
          </RightContent>
        </HeaderContainer>
      </Trigger>
      {dividerOnOpen && isOpen && <Divider margin="0" />}
      <Content>
        <ContentWrapper>{children}</ContentWrapper>
      </Content>
      {!isBordered && <Divider margin="0" />}
    </Root>
  );
}

const Root = styled(RadixCollapsible)<{
  isBordered: boolean;
  removeTopBorderOnExpand?: boolean;
  isOpen?: boolean;
}>(({ theme, isBordered, removeTopBorderOnExpand, isOpen }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  ...(isBordered && {
    border: `1px solid ${theme.colors.sysBorderPrimary}`,
    borderRadius: 8,

    ...(removeTopBorderOnExpand &&
      isOpen && {
        borderTop: "none",
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }),
  }),
}));

const Trigger = styled(CollapsibleTrigger)<{ disabled?: boolean }>(({ theme, disabled }) => ({
  all: "unset",
  minHeight: 64,
  padding: "0 24px",
  position: "relative",
  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
    transition: smoothTransition,
  },
  "&:focus-visible": {
    borderRadius: "2px",
    border: `2px solid ${theme.colors.sysPrimaryDefault}`,
  },
  cursor: disabled ? "not-allowed" : "pointer",
}));

const HeaderContainer = styled.div<{ chevronPosition?: ChevronPosition }>(
  ({ chevronPosition }) => ({
    display: "flex",
    alignItems: chevronPosition === "top" ? "baseline" : "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
  }),
);

const HeaderContent = styled.div<{ padding?: string }>(({ padding = "12px 0" }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flex: 1,
  minWidth: 0,
  padding: padding,
}));

const RightContent = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const TitleContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  flex: 1,
  minWidth: 0,
});

const Title = styled(Text)<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.colors.sysTextInactive : theme.colors.sysTextDefault,
  wordWrap: "break-word",
  overflow: "hidden",
}));

const Subtitle = styled(Text)<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.colors.sysTextInactive : theme.colors.sysTextSecondary,
}));

const ChevronContainer = styled.div<{ disabled?: boolean; open?: boolean }>(
  ({ disabled, open, theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: disabled ? theme.colors.sysTextInactive : theme.colors.sysTextDefault,
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: smoothTransition,
  }),
);

const Content = styled(CollapsibleContent)({
  overflow: "hidden",
  "&[data-state='open']": {
    animation: "slideDown 300ms ease-out",
  },
  "&[data-state='closed']": {
    animation: "slideUp 300ms ease-out",
  },
  "@keyframes slideDown": {
    from: {
      height: 0,
    },
    to: {
      height: "var(--radix-collapsible-content-height)",
    },
  },
  "@keyframes slideUp": {
    from: {
      height: "var(--radix-collapsible-content-height)",
    },
    to: {
      height: 0,
    },
  },
});

const ContentWrapper = styled.div({
  padding: "0 24px 24px",
});
